import React, { useState, useEffect } from "react";
import { 
  Typography, 
  Stack, 
  Chip, 
  Box, 
  Skeleton, 
  Alert,
  TextField,
  Button,
  IconButton,
  Tooltip,
  CircularProgress
} from "@mui/material";
import { styled } from "@mui/material/styles";

import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";

import { useSelector } from "react-redux";
import { db, firefuncs } from "config/firebase";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { RootState } from "types";

import UpdatePhoneNumber from "./UpdatePhoneNumber.jsx";

const InfoItem = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "transparent",
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const InfoLabel = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
  minWidth: "120px",
}));

interface UserData {
  joinDate: string;
  displayName?: string;
  email?: string;
  phoneNumber?: string;
  [key: string]: any;
}

interface UserInfoProps {
  userId: string;
}

interface UpdateDisplayNameResponse {
  success: boolean;
  message: string;
  remainingChanges: number;
}

interface GetDisplayNameChangesResponse {
  remainingChanges: number;
  totalChanges: number;
}

function UserInfo({ userId }: UserInfoProps) {
  const user = useSelector((state: RootState) => state.user.user);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Display name editing state
  const [isEditingDisplayName, setIsEditingDisplayName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState("");
  const [displayNameLoading, setDisplayNameLoading] = useState(false);
  const [displayNameError, setDisplayNameError] = useState<string | null>(null);
  const [displayNameSuccess, setDisplayNameSuccess] = useState<string | null>(null);
  const [remainingChanges, setRemainingChanges] = useState<number | null>(null);

  // Cloud functions
  const updateDisplayNameWithLimit = httpsCallable<
    { displayName: string },
    UpdateDisplayNameResponse
  >(firefuncs, "updateDisplayNameWithLimit");

  const getDisplayNameChanges = httpsCallable<
    Record<string, never>,
    GetDisplayNameChangesResponse
  >(firefuncs, "getDisplayNameChanges");

  useEffect(() => {
    getUserData();
    fetchRemainingChanges();
  }, [userId]);

  const fetchRemainingChanges = async () => {
    try {
      const result = await getDisplayNameChanges({});
      setRemainingChanges(result.data.remainingChanges);
    } catch (error) {
      console.error("Error fetching remaining changes:", error);
      setRemainingChanges(2); // Default to 2 if we can't fetch
    }
  };

  const handleEditDisplayName = () => {
    setNewDisplayName(userData?.displayName || "");
    setIsEditingDisplayName(true);
    setDisplayNameError(null);
    setDisplayNameSuccess(null);
  };

  const handleCancelEditDisplayName = () => {
    setIsEditingDisplayName(false);
    setNewDisplayName("");
    setDisplayNameError(null);
    setDisplayNameSuccess(null);
  };

  const handleSaveDisplayName = async () => {
    if (!newDisplayName.trim()) {
      setDisplayNameError("Display name cannot be empty");
      return;
    }

    if (newDisplayName.trim() === userData?.displayName) {
      setDisplayNameError("New display name is the same as current display name");
      return;
    }

    setDisplayNameLoading(true);
    setDisplayNameError(null);

    try {
      const result = await updateDisplayNameWithLimit({
        displayName: newDisplayName.trim(),
      });

      if (result.data.success) {
        setDisplayNameSuccess(result.data.message);
        setRemainingChanges(result.data.remainingChanges);
        setIsEditingDisplayName(false);
        setNewDisplayName("");
        
        // Reload user data to reflect changes
        await getUserData();
      }
    } catch (err: any) {
      console.error("Error updating display name:", err);
      setDisplayNameError(err.message || "Failed to update display name");
    } finally {
      setDisplayNameLoading(false);
    }
  };

  const getUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      const userRef = doc(db, "users", userId);
      const docSnap = await getDoc(userRef);

      if (docSnap.exists()) {
        setUserData(docSnap.data() as UserData);
      } else {
        setError("User information not found.");
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      setError("Failed to load user information.");
    } finally {
      setLoading(false);
    }
  };

  const formatJoinDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <PersonIcon color="primary" />
          User Information
        </Typography>
        <Stack spacing={2}>
          <Skeleton variant="text" height={40} />
          <Skeleton variant="text" height={40} />
          <Skeleton variant="text" height={40} />
          <Skeleton variant="text" height={40} />
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <PersonIcon color="primary" />
          User Information
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!userData) {
    return null;
  }

  const renderDisplayNameSection = () => {
    if (isEditingDisplayName) {
      return (
        <InfoItem>
          <PersonIcon color="action" />
          <InfoLabel variant="body2">Display Name:</InfoLabel>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
            <TextField
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              size="small"
              placeholder="Enter new display name"
              error={!!displayNameError}
              helperText={displayNameError}
              inputProps={{ maxLength: 30 }}
              sx={{ maxWidth: 300 }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                onClick={handleSaveDisplayName}
                variant="contained"
                size="small"
                startIcon={displayNameLoading ? <CircularProgress size={16} /> : <SaveIcon />}
                disabled={displayNameLoading}
              >
                {displayNameLoading ? "Saving..." : "Save"}
              </Button>
              <Button
                onClick={handleCancelEditDisplayName}
                variant="outlined"
                size="small"
                startIcon={<CancelIcon />}
                disabled={displayNameLoading}
              >
                Cancel
              </Button>
            </Stack>
          </Box>
        </InfoItem>
      );
    }

    return (
      <InfoItem>
        <PersonIcon color="action" />
        <InfoLabel variant="body2">Display Name:</InfoLabel>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {userData.displayName || "Not set"}
          </Typography>
          {remainingChanges !== 0 && (
            <Tooltip title="Edit display name">
              <IconButton onClick={handleEditDisplayName} size="small">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </InfoItem>
    );
  };

  return (
    <Box>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <PersonIcon color="primary" />
        User Information
      </Typography>

      {/* Success/Error Messages */}
      {displayNameSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDisplayNameSuccess(null)}>
          {displayNameSuccess}
        </Alert>
      )}
      {displayNameError && !isEditingDisplayName && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDisplayNameError(null)}>
          {displayNameError}
        </Alert>
      )}

      {/* Remaining Changes Info - only show when editing */}
      {isEditingDisplayName && remainingChanges !== null && (
        <Box sx={{ mb: 2 }}>
          <Chip
            label={
              remainingChanges === 0
                ? "No display name changes remaining"
                : `${remainingChanges} display name change(s) remaining`
            }
            size="small"
            color={remainingChanges === 0 ? "error" : "info"}
            variant="outlined"
          />
        </Box>
      )}

      <Stack spacing={1}>
        {renderDisplayNameSection()}

        <InfoItem>
          <EmailIcon color="action" />
          <InfoLabel variant="body2">Email:</InfoLabel>
          <Typography variant="body1">{user.email}</Typography>
        </InfoItem>

        <InfoItem>
          <PhoneIcon color="action" />
          <InfoLabel variant="body2">Phone:</InfoLabel>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {user.phoneNumber ? (
              <Chip
                label={user.phoneNumber}
                variant="outlined"
                size="small"
                color="success"
              />
            ) : (
              <UpdatePhoneNumber />
            )}
          </Box>
        </InfoItem>

        <InfoItem>
          <CalendarTodayIcon color="action" />
          <InfoLabel variant="body2">Member Since:</InfoLabel>
          <Typography variant="body1">
            {formatJoinDate(userData.joinDate)}
          </Typography>
        </InfoItem>
      </Stack>
    </Box>
  );
}

export default UserInfo;
